import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";

/**
 * Confeti de celebración, una vez.
 *
 * ── Por qué no es el componente de la librería de shadcn ─────────────
 * El ejemplo que circula trae su propio `Button`, su `cn` y la
 * estructura `components/ui` de shadcn. Este proyecto tiene su design
 * system (`shared/ui`) y no usa shadcn: copiarlo entero metería un
 * segundo sistema de botones para pintar papelitos. Aquí queda solo lo
 * que hace falta —`canvas-confetti` sobre un lienzo propio— y el botón,
 * los colores y la card los pone quien celebra.
 *
 * ── Un lienzo propio, no el global ───────────────────────────────────
 * `confetti.create(canvas)` en vez del `confetti()` suelto: el lienzo
 * vive dentro de la card, se limpia al desmontar y no deja un `<canvas>`
 * pegado al `<body>` capturando clics.
 */
export function Confetti({ fire = true }: { fire?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !fire) return;

    /*
     * Sin contexto 2D no hay confeti, y no pasa nada: jsdom (las
     * pruebas) no implementa `getContext`, y algunos navegadores lo
     * bloquean por privacidad. Celebrar es un adorno — que reviente la
     * pantalla por unos papelitos sería absurdo.
     */
    let context: unknown = null;
    try {
      context = canvas.getContext("2d");
    } catch {
      context = null;
    }
    if (!context) return;

    // `useWorker: false` a propósito: con worker, jsdom (las pruebas) y
    // algún navegador viejo tiran una excepción al crear el hilo.
    const shoot = confetti.create(canvas, { resize: true, useWorker: false });
    const colors = ["#5C4FD6", "#9B8BC4", "#DCD4F0", "#C9A96A", "#ffffff"];

    // Dos ráfagas desde los lados, como una traca corta: una sola
    // explosión central tapa el mensaje que se está celebrando.
    void shoot({ particleCount: 60, spread: 70, angle: 60, origin: { x: 0, y: 0.7 }, colors });
    void shoot({ particleCount: 60, spread: 70, angle: 120, origin: { x: 1, y: 0.7 }, colors });
    const second = window.setTimeout(() => {
      void shoot({ particleCount: 40, spread: 100, origin: { x: 0.5, y: 0.4 }, colors, scalar: 0.9 });
    }, 250);

    return () => {
      window.clearTimeout(second);
      try {
        shoot.reset();
      } catch {
        // Nada que limpiar si nunca llegó a dibujar.
      }
    };
  }, [fire]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
